import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import type { MemoMoveTarget } from "@/lib/memoMoveTargets";

interface Props {
  /** The rows to offer — `lib/memoMoveTargets.ts::memoMoveTargets`. */
  targets: MemoMoveTarget[];
  /** Move this entry. A sub-heading takes its points along, server-side. */
  onMove: (to: { blockId: string; subheadingId?: string | null }) => void;
}

function pickableItems(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(
    menu?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [],
  );
}

function focusItem(el: HTMLElement | undefined) {
  // preventScroll + nearest: walking the list must not drag the (presented) page along.
  el?.focus({ preventScroll: true });
  el?.scrollIntoView?.({ block: "nearest" });
}

/** Hover action to move a point into another Abschnitt or Zwischenabschnitt (a
 * Zwischenüberschrift: into another Abschnitt). A compact, scrollable two-level list —
 * Abschnitte with their Zwischenabschnitte indented beneath. Hidden when there is nowhere
 * to move to. */
export function MemoMoveMenu({ targets, onMove }: Props) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Keyboard users land IN the menu, so Escape and the arrows reach it.
  useEffect(() => {
    if (open) focusItem(pickableItems(menuRef.current)[0]);
  }, [open]);
  if (!targets.some((x) => !x.current)) return null;
  const nestedIn = new Set(targets.filter((x) => x.nested).map((x) => x.blockId));

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        title={t.memoMoveTitle}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 3,
          lineHeight: 0,
        }}
      >
        <Icon name="moveRight" size={14} color="var(--ink-4)" />
      </button>
      {open ? (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            role="menu"
            aria-label={t.memoMoveTo}
            onKeyDown={(e) => {
              // The innermost surface owns these keys: while presenting, Escape would also
              // leave the presentation and the arrows would move its cursor.
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                const items = pickableItems(menuRef.current);
                const i = items.indexOf(document.activeElement as HTMLElement);
                const step = e.key === "ArrowDown" ? 1 : -1;
                focusItem(items[(i + step + items.length) % items.length]);
              }
            }}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              zIndex: 41,
              minWidth: 200,
              maxWidth: 300,
              // Long Protokolle carry many headings: the list scrolls, the page does not.
              maxHeight: 320,
              overflowY: "auto",
              background: "var(--surface-elev)",
              border: "1px solid var(--hairline-strong)",
              borderRadius: 10,
              boxShadow: "0 12px 30px rgba(13,62,99,0.22)",
              padding: 6,
            }}
          >
            <div
              role="presentation"
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--ink-4)",
                padding: "4px 8px 3px",
              }}
            >
              {t.memoMoveTo}
            </div>
            {targets.map((target, i) => {
              const label = target.label.trim() || t.memoMoveUntitled;
              const row: React.CSSProperties = {
                display: "flex",
                alignItems: "baseline",
                gap: 6,
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                paddingTop: target.nested ? 4 : 6,
                paddingBottom: target.nested ? 4 : 5,
                paddingRight: 8,
                // A Zwischenabschnitt sits one step in, under its Abschnitt.
                paddingLeft: target.nested ? 22 : 8,
                marginTop: !target.nested && i > 0 ? 4 : 0,
                fontSize: target.nested ? 12.5 : 13,
                fontWeight: target.nested ? 400 : 600,
                color: target.nested ? "var(--ink-2)" : "var(--ink-1)",
                minWidth: 0,
              };
              const text: React.CSSProperties = {
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              };
              const key = `${target.blockId}:${target.subheadingId ?? ""}`;
              if (target.current) {
                // Where the point already is — orientation only, never a target.
                return (
                  <div
                    key={key}
                    role="menuitem"
                    aria-disabled="true"
                    style={{ ...row, color: "var(--ink-4)", cursor: "default" }}
                  >
                    <span style={text}>{label}</span>
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 400 }}>
                      · {t.memoMoveHere}
                    </span>
                  </div>
                );
              }
              return (
                <button
                  key={key}
                  type="button"
                  role="menuitem"
                  title={
                    !target.nested && nestedIn.has(target.blockId)
                      ? `${label} – ${t.memoMoveTopLevelHint}`
                      : label
                  }
                  onClick={() => {
                    // `subheadingId` undefined (a heading move) stays undefined all the way to
                    // the wire, where it is omitted — NOT null, which means "top level".
                    onMove({ blockId: target.blockId, subheadingId: target.subheadingId });
                    setOpen(false);
                  }}
                  style={{ ...row, cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span style={text}>{label}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
